import { COUNTDOWN_DURATION_SECONDS } from '@/components/version-reload/constants';
import { ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';

interface VersionReloadToastProps {
  countdownSeconds: number;
  isCountingDown: boolean;
  onRefresh: () => void;
  onLater: () => void;
}

const RADIUS = 16;
const STROKE_WIDTH = 3;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const VersionReloadToast = ({
  countdownSeconds,
  isCountingDown,
  onRefresh,
  onLater,
}: VersionReloadToastProps) => {
  const normalizedSeconds = Math.max(0, Math.min(COUNTDOWN_DURATION_SECONDS, countdownSeconds));
  const progress = normalizedSeconds / COUNTDOWN_DURATION_SECONDS;
  const dashOffset = isCountingDown ? CIRCUMFERENCE * (1 - progress) : 0;

  return (
    <div className="pointer-events-auto w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-lg ring-1 ring-black/5">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">A new version is available</p>
            <p className="mt-1 text-sm text-slate-600">
              {isCountingDown
                ? 'Refreshing automatically while the countdown is running.'
                : 'Refresh now, or wait for the idle countdown to start automatically.'}
            </p>
          </div>
          <div className="flex shrink-0 items-center justify-center">
            <div
              className="relative h-10 w-10"
              aria-label={
                isCountingDown
                  ? `Refreshing in ${normalizedSeconds} seconds`
                  : 'Automatic refresh countdown will start when the app is idle'
              }
            >
              <svg className="h-10 w-10 -rotate-90" viewBox="0 0 40 40" aria-hidden="true">
                <circle
                  cx="20"
                  cy="20"
                  r={RADIUS}
                  fill="none"
                  stroke="rgb(226 232 240)"
                  strokeWidth={STROKE_WIDTH}
                />
                <circle
                  cx="20"
                  cy="20"
                  r={RADIUS}
                  fill="none"
                  stroke={isCountingDown ? 'rgb(245 158 11)' : 'rgb(148 163 184)'}
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                  strokeWidth={STROKE_WIDTH}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-slate-700">
                {isCountingDown ? (
                  normalizedSeconds
                ) : (
                  <ClockIcon className="h-4 w-4" aria-hidden="true" />
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            onClick={onLater}
          >
            Later
          </button>
          <button
            type="button"
            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
            onClick={onRefresh}
          >
            Refresh now
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionReloadToast;
